import { KatAlert, KatButton, KatSpinner } from '@amzn/katal-react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SITE_ID_PARAM_NAME } from 'src/core/config/navigationRoutes';
import { useFCConfig } from 'src/features/hooks/useFCConfig';
import { useUser } from 'src/utils/UserContext';
import { triggerCategorizationGeneration } from 'src/utils/Queries';
import './FCConfigPage.css';

const FCConfigPage: React.FC = () => {
    const { user } = useUser();
    const { [SITE_ID_PARAM_NAME]: siteId } = useParams();
    const { data, isLoading, error } = useFCConfig(siteId);
    const [triggerStatus, setTriggerStatus] = useState<{ success: boolean; message: string } | null>(null);
    const [isTriggering, setIsTriggering] = useState(false);

    if (!user?.isDevAdmin) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Access Denied">Developer access required.</KatAlert></div>;
    }

    if (!siteId) {
        return <div className="fc-config-page"><KatAlert variant="info" header="No Site Selected">Provide a site ID in the URL (e.g., /fcconfig/BFI4).</KatAlert></div>;
    }

    if (isLoading) {
        return <div className="fc-config-page"><KatSpinner /></div>;
    }

    if (error) {
        return <div className="fc-config-page"><KatAlert variant="danger" header="Error">{error.message}</KatAlert></div>;
    }

    const categorizationType = data?.productionCapabilityMappingType === 'ONE_DC' ? 'HOV' : 'PICKING';

    const handleRegenerate = async () => {
        setIsTriggering(true);
        setTriggerStatus(null);
        try {
            const response = await triggerCategorizationGeneration(siteId, categorizationType);
            setTriggerStatus({ success: response?.success ?? false, message: response?.message ?? 'No response' });
        } catch (e: any) {
            setTriggerStatus({ success: false, message: e.message });
        } finally {
            setIsTriggering(false);
        }
    };

    return (
        <div className="fc-config-page">
            <h2>FC Configuration — {siteId}</h2>

            <div className="fc-config-card">
                <div className="fc-config-section-header">Configuration</div>
                <div className="fc-config-grid">
                    <div className="fc-config-item">
                        <span className="fc-config-label">FC Name</span>
                        <span className="fc-config-value">{data?.fcName ?? '-'}</span>
                    </div>
                    <div className="fc-config-item">
                        <span className="fc-config-label">PC Generation Model</span>
                        <span className="fc-config-value">{data?.productionCapabilityMappingType ?? '-'}</span>
                    </div>
                    <div className="fc-config-item">
                        <span className="fc-config-label">Rule Generation Model</span>
                        <span className="fc-config-value">{data?.categorizationGenerationType ?? '-'}</span>
                    </div>
                    <div className="fc-config-item">
                        <span className="fc-config-label">Categorization Type</span>
                        <span className="fc-config-value fc-config-badge">{categorizationType}</span>
                    </div>
                </div>
            </div>

            <div className="fc-config-card">
                <div className="fc-config-section-header">Actions</div>
                <p className="fc-config-description">
                    Trigger categorization rule regeneration for this FC. This will invoke the
                    CategorizationGenerationTriggerLambda with type <strong>{categorizationType}</strong>.
                </p>
                <div className="fc-config-actions">
                    <KatButton
                        variant="primary"
                        onClick={handleRegenerate}
                        disabled={isTriggering}
                        label={isTriggering ? 'Triggering...' : `Regenerate ${categorizationType} Rules`}
                    />
                </div>
                {triggerStatus && (
                    <div className="fc-config-status-container">
                        <KatAlert
                            variant={triggerStatus.success ? 'success' : 'danger'}
                            header={triggerStatus.success ? 'Success' : 'Failed'}
                        >
                            {triggerStatus.message}
                        </KatAlert>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FCConfigPage;
